import { useMutation, useQuery } from '@apollo/client';
import { OnSubmit, useRuleBuilderApi } from '@procureai/rule-builder-ui-mui';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useCallback, useMemo } from 'react';

import { GET_RULE_BUIDER_BACKEND_DATA } from '../../hooks/useRuleBuilderBackend/Rule.query.gql';
import { TsrctRuleStatus, TsrctRuleVersionStatus } from '@/generated/gql/graphql.ts';

import { GET_RULE_VERSION_STATUSES } from './Rule.query.gql';
import { PAUSE_RULE_VERSION, PUBLISH_RULE_VERSION, SAVE_RULE_VERSION } from './RuleVersion.mutation.gql';

interface UseRuleVersionActionsProps {
  id: string;
}

const getEnabledActions = (ruleStatus: TsrctRuleStatus, ruleVersionStatus: TsrctRuleVersionStatus) => {
  const isInitialDraft = ruleStatus === TsrctRuleStatus.DRAFT && ruleVersionStatus === TsrctRuleVersionStatus.DRAFT;
  const isPublished = ruleStatus === TsrctRuleStatus.PUBLISHED;
  const isPaused = ruleStatus === TsrctRuleStatus.PAUSED;
  const isRemovable = ruleStatus !== TsrctRuleStatus.PUBLISHED;
  const isModified =
    (ruleStatus === TsrctRuleStatus.PUBLISHED || ruleStatus === TsrctRuleStatus.PAUSED) &&
    ruleVersionStatus === TsrctRuleVersionStatus.DRAFT;

  return {
    isRemovable,
    isPauseable: isPublished,
    isPublishable: isModified || isPaused || isInitialDraft,
    hasUnpublishedChanges: isModified,
  };
};

export const useRuleVersionActions = ({ id }: UseRuleVersionActionsProps) => {
  const { onError } = useCustomSnackbar();

  const { submit } = useRuleBuilderApi();

  const { data, loading, refetch } = useQuery(GET_RULE_VERSION_STATUSES, { variables: { id }, skip: !id, onError });

  const rule = data?.tesseract.rule;
  const ruleVersionId = rule?.ruleVersion?.id;
  const ruleStatus = rule?.status ?? TsrctRuleStatus.DRAFT;
  const latestRuleVersionStatus = rule?.ruleVersion?.status ?? TsrctRuleVersionStatus.DRAFT;
  const isDraftRuleVersion = latestRuleVersionStatus === TsrctRuleVersionStatus.DRAFT;

  const enabledActionStatuses = useMemo(
    () => getEnabledActions(ruleStatus, latestRuleVersionStatus),
    [ruleStatus, latestRuleVersionStatus],
  );

  // Refetching queries to determine the current rule status & retrieving most recent rule version graph
  const refetchQueries = useMemo(() => [GET_RULE_BUIDER_BACKEND_DATA, GET_RULE_VERSION_STATUSES], []);

  const [publishMutation, { loading: publishLoading }] = useMutation(PUBLISH_RULE_VERSION, {
    onError,
    refetchQueries,
  });
  const [pauseMutation, { loading: pauseLoading }] = useMutation(PAUSE_RULE_VERSION, {
    onError,
    refetchQueries,
  });
  const [saveMutation, { loading: saveLoading }] = useMutation(SAVE_RULE_VERSION, {
    onError,
  });

  const publish = useCallback(() => submit(true), [submit]);
  const save = useCallback(() => submit(false), [submit]);

  const pause = useCallback(async () => {
    await pauseMutation({ variables: { id } });
  }, [id, pauseMutation]);

  const onSubmit = useCallback<OnSubmit>(
    async (graph, validated) => {
      let savedVersionId = ruleVersionId;

      if (!validated || isDraftRuleVersion) {
        const saveResult = await saveMutation({ variables: { id, graph } });
        if (saveResult.errors) {
          // error handling is done in the mutation itself
          return;
        }
        savedVersionId = saveResult.data?.tesseract.rule.ruleVersion?.save.id;
        refetch();
      }

      // we only validate on publishing right now, thus property
      // "validated" is a decision marker for publishing vs saving
      if (validated === true) {
        await publishMutation({ variables: { id, versionId: savedVersionId } });
      }
    },
    [id, isDraftRuleVersion, ruleVersionId, refetch, publishMutation, saveMutation],
  );

  return {
    loading: loading || saveLoading || publishLoading || pauseLoading,
    ...enabledActionStatuses,
    publish,
    pause,
    save,
    onSubmit,
  };
};
